import {
  Button,
  IconWithBackground,
  InternalIcon,
  Link,
  Stack,
  useTheme,
  IconWithBackgroundColor,
  Typography,
  Box
} from "@carrotfertility/carotene-core"
import React, { ReactElement } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { ValueProposition } from "./hooks/useBenefitHighlights"
import { Paths } from "#/utils/Paths"
import { useHistory } from "react-router"
import { Link as ReactRouterLink } from "react-router-dom"
import { useContentfulBenefitHighlights } from "#/components/home/benefits-modules/hooks/useContentfulBenefitHighlights"
import { ContentfulContentRenderer } from "#/services/contentful"

type BenefitHighlightHeroProps = {
  filteredValueProps: ValueProposition[]
}

interface BenefitHighlightsProps {
  filteredValueProps: ValueProposition[]
  color: string
}

interface ValuePropDisplayProps {
  valueProp: ValueProposition
  color: string
  key: any
}

function ValuePropDisplay({ valueProp, color }: ValuePropDisplayProps): ReactElement {
  const theme = useTheme()
  const intl = useIntl()
  return (
    <Link
      component={ReactRouterLink}
      to={valueProp.link}
      color={theme.palette.text.primary}
      startIcon={<IconWithBackground icon={valueProp.icon} color={color as IconWithBackgroundColor} />}
      endIcon={<InternalIcon fontSize="small" color="primary" />}
    >
      {intl.formatMessage(valueProp.valuePropMessage)}
    </Link>
  )
}

export function CalibratedEngagementCta(): ReactElement {
  const history = useHistory()

  return (
    <Stack alignItems={"start"}>
      <Button
        onClick={() => history.push(Paths.YOUR_BENEFIT_GUIDE)}
        color="secondary"
        variant="contained"
        fullWidth={false}
      >
        <FormattedMessage defaultMessage="View benefit guide" />
      </Button>
    </Stack>
  )
}

export function BenefitHighlightsInner({ filteredValueProps, color }: BenefitHighlightsProps): ReactElement {
  const theme = useTheme()

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
      {filteredValueProps.map((valueProp: ValueProposition) => (
        <ValuePropDisplay valueProp={valueProp} color={color} key={valueProp.valuePropId} />
      ))}
    </Stack>
  )
}

export function BenefitHighlightsContent({ filteredValueProps }: BenefitHighlightHeroProps): JSX.Element {
  return (
    <>
      <Typography variant="h3">
        <FormattedMessage defaultMessage="Benefit highlights" />
      </Typography>
      <BenefitHighlightsInner filteredValueProps={filteredValueProps} color="primary" />
    </>
  )
}

export function ContentfulBenefitHighlightsContent(): ReactElement {
  const { content, programTypeAllowsBenefitHighlight, isLoading, isError } = useContentfulBenefitHighlights()

  if (isLoading || isError || !content || !programTypeAllowsBenefitHighlight) {
    return null
  }

  return (
    <Box data-testid="contentful-benefit-highlights">
      <ContentfulContentRenderer contents={content} />
    </Box>
  )
}
